import { Equalizer } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useMemo } from 'react';

import { Button } from '@/components/Button';
import { AddAlertParams } from '@/configs/store/AlertsStore';
import { useAlertsStore } from '@/configs/store/useRootStore';
import { useDraftTimelogStore } from '@/features/draftTimelogs';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { viewSettingsStore } from '@/features/viewSettings';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getColorByMinutes } from '@/utils/getColorByMinutes';

import { useTimeLogStore } from '../../model';

const EMPTY_STATS_ALERT: AddAlertParams = {
  message: 'Нет данных для отображения',
  title: 'Статистика',
  icon: Equalizer,
  autoClose: true,
  withReload: false,
};

const StatsButton = () => {
  const { view, workTime } = viewSettingsStore;
  const { isViewingSelf } = useGlobalUserFilterModel();
  const { filterData: timesheetData, currentDate } = useTimeLogStore();
  const { totalDraftMinutes, draftFilter } = useDraftTimelogStore();

  const { addAlert } = useAlertsStore();

  const myselfWorkTime = isViewingSelf ? Number(workTime) : 40;

  const combinedTotalMinutes = useMemo(() => {
    const logMinutes = draftFilter !== 'drafts' ? (timesheetData?.totalMinutes ?? 0) : 0;
    const draftMins = isViewingSelf && draftFilter !== 'logs' ? totalDraftMinutes : 0;

    return logMinutes + draftMins;
  }, [timesheetData?.totalMinutes, totalDraftMinutes, draftFilter, isViewingSelf]);

  const colorByMinutes = useMemo(
    () => getColorByMinutes(combinedTotalMinutes, myselfWorkTime, view, currentDate),
    [combinedTotalMinutes, myselfWorkTime, view, currentDate],
  );

  const logMinutesForTooltip = draftFilter !== 'drafts' ? (timesheetData?.totalMinutes ?? 0) : 0;
  const draftMinutesForTooltip = isViewingSelf && draftFilter !== 'logs' ? totalDraftMinutes : 0;

  const statsTooltip: ReactNode = useMemo(() => {
    if (draftMinutesForTooltip > 0) {
      return (
        <>
          Статистика
          <br />
          Залогировано: {formatDurationRussian(logMinutesForTooltip)}
          <br />
          Черновики: {formatDurationRussian(draftMinutesForTooltip)}
        </>
      );
    }

    return 'Статистика';
  }, [draftMinutesForTooltip, logMinutesForTooltip]);

  const handleClick = () => {
    if (combinedTotalMinutes === 0) {
      addAlert(EMPTY_STATS_ALERT);

      return;
    }

    modalManagerStore.open('stats');
  };

  return (
    <Tooltip title={statsTooltip} arrow>
      <Button
        variant="outlined"
        onClick={handleClick}
        sx={{
          borderColor: colorByMinutes,
          color: colorByMinutes,
          borderRadius: '6px',
          boxShadow: 'none',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          transition: 'border-color 0.4s, color 0.4s',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderColor: colorByMinutes,
            color: colorByMinutes,
          },
        }}
      >
        <Equalizer sx={{ width: '18px' }} />
        <p>{combinedTotalMinutes > 0 ? formatDurationRussian(combinedTotalMinutes) : '0ч'}</p>
      </Button>
    </Tooltip>
  );
};

export default observer(StatsButton);
